import React, { useState, createContext, useContext, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom';
import LoginPage from './pages/login/Login';
import HomePage from './pages/home/Home';
import SignUpPage from './pages/signup/SignUp';
import PublishPage from './pages/publish/Publish';
import VideoManagement from './pages/videoManagement/VideoManagement.tsx';
import BillingPage from './pages/billing/BillingPage';
import SuccessPage from './pages/billing/SuccessPage';
import CancelledPage from './pages/billing/CancelledPage';
import ForgotPasswordPage from './pages/forgotPasword/ForgotPassword.tsx';
import ResetPasswordPage from './pages/resetPassword/ResetPassword.tsx';
import LegalPage from './pages/legal/LegalPage.tsx';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PublishError from './pages/publish/PublishError';
import {
  trackClarityPage,
  identifyClarityUser,
} from './services/clarityService';
import { identifySentryUser } from './services/sentryService';
// TEMP (no-consent-gate experiment): banner unmounted to remove signup
// friction — re-enable the import + usage below to restore the gate.
// import ClarityConsentBanner from './components/clarityconsent/ClarityConsentBanner';
import {
  IDLE_PUBLISH_JOB,
  PublishJob,
} from './types/publishJob.ts';

const AuthContext = createContext<{
  isAuthenticated: boolean;
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
}>({
  isAuthenticated: false,
  setIsAuthenticated: () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return !!localStorage.getItem('authToken'); // Initialize from localStorage
  });

  useEffect(() => {
    const token = localStorage.getItem('authToken');

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1])); // Decode JWT payload
        const isExpired = payload.exp * 1000 < Date.now(); // Convert exp to milliseconds

        if (!isExpired) {
          setIsAuthenticated(true);
        } else {
          console.log('Token expired, removing from storage');
          localStorage.removeItem('authToken'); // Clear expired token
        }
      } catch (error) {
        console.error('Invalid token format', error);
        localStorage.removeItem('authToken'); // Remove invalid token
      }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, setIsAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({
  element,
}) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    console.log('User not authenticated, redirecting to login page');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return element;
};

/**
 * Renders nothing but tracks the current route in Clarity and re-identifies
 * the logged-in user on every page (Clarity's Identify API should be called
 * for each page). Must be rendered inside the Router so it can use useLocation.
 */
const ClarityTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const pageName = location.pathname + location.search;
    trackClarityPage(pageName);

    try {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      identifyClarityUser(storedUser);
      identifySentryUser(storedUser);
    } catch {
      /* ignore malformed stored user */
    }
  }, [location]);

  return null;
};

const App = () => {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [publishJob, setPublishJob] = useState<PublishJob>(IDLE_PUBLISH_JOB);
  return (
    <>
      {/* TEMP: consent gate disabled — uncomment to restore the mandatory banner. */}
      {/* <ClarityConsentBanner /> */}
      <ToastContainer position="top-right" autoClose={3000} />
      <AuthProvider>
        <Router>
          <ClarityTracker />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/terms" element={<LegalPage kind="terms" />} />
            <Route path="/privacy" element={<LegalPage kind="privacy" />} />
            <Route
              path="/"
              element={<ProtectedRoute element={<VideoManagement />} />}
            />
            <Route
              path="/video/:id"
              element={
                <ProtectedRoute
                  element={
                    <HomePage
                      setBlob={setBlob}
                      publishJob={publishJob}
                      setPublishJob={setPublishJob}
                    />
                  }
                />
              }
            />
            <Route
              path="/publish"
              element={
                <ProtectedRoute
                  element={
                    <PublishPage
                      blob={blob}
                      setBlob={setBlob}
                      publishJob={publishJob}
                    />
                  }
                />
              }
            />
            <Route
              path="/billing"
              element={<ProtectedRoute element={<BillingPage />} />}
            />
            <Route
              path="/billing/success"
              element={<ProtectedRoute element={<SuccessPage />} />}
            />
            <Route
              path="/billing/cancelled"
              element={<ProtectedRoute element={<CancelledPage />} />}
            />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/recording-error"
              element={<PublishError setBlob={setBlob} />}
            />
          </Routes>
        </Router>
      </AuthProvider>
    </>
  );
};

export default App;
